import { useMemo, useCallback, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { REPORT_CONFIG_KEYS } from '../constants';
import { abyssColors } from '@/theme/colors';
import { titleCaseFromKey } from '@/utils';

const mid = Math.ceil(REPORT_CONFIG_KEYS.length / 2);
const COLUMNS = [REPORT_CONFIG_KEYS.slice(0, mid), REPORT_CONFIG_KEYS.slice(mid)];

type ReportConfig = {
  key: string;
  title: string;
};

type ReportConfigListProps = {
  mode: 'ReadOnly' | 'Edit';
  reportConfig: ReportConfig[];
  onToggleKey?: (key: string, nextChecked: boolean) => void;
  onUpdateTitle: (key: string, nextTitle: string) => Promise<void> | void;
};

type KeyRowProps = {
  mode: 'ReadOnly' | 'Edit';
  keyName: string;
  existing?: ReportConfig | undefined;
  checked: boolean;
  isEditing: boolean;
  draftTitle: string;
  onToggle: (keyName: string, next: boolean) => void;
  onStartEdit: (keyName: string) => void;
  onChangeDraft: (v: string) => void;
  onSave: (keyName: string) => void | Promise<void>;
  onCancel: () => void;
};

const KeyRow = ({
  mode,
  keyName,
  existing,
  checked,
  isEditing,
  draftTitle,
  onToggle,
  onStartEdit,
  onChangeDraft,
  onSave,
  onCancel,
}: KeyRowProps) => {
  const label = useMemo(() => titleCaseFromKey(keyName), [keyName]);
  const readOnly = mode === 'ReadOnly';

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        await onSave(keyName);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [keyName, onSave, onCancel]
  );

  return (
    <Box key={keyName}>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={(e) => onToggle(keyName, e.target.checked)}
            disabled={readOnly}
            inputProps={{ 'aria-label': `Toggle ${label}` }}
          />
        }
        label={
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
        }
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: 5,
          mt: 0.5,
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Title:
        </Typography>

        {isEditing && mode === 'Edit' ? (
          <>
            <TextField
              variant="standard"
              value={draftTitle}
              onChange={(e) => onChangeDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              fullWidth
              placeholder="Enter title"
              disabled={readOnly}
              slotProps={{
                input: {
                  disableUnderline: true,
                  endAdornment: (
                    <>
                      <Tooltip title="Save">
                        <span>
                          <IconButton
                            onClick={() => onSave(keyName)}
                            disabled={readOnly || !draftTitle.trim()}
                            size="small"
                            sx={{ color: abyssColors.primary[500] }}
                          >
                            <CheckIcon sx={{ fontSize: '1.5rem' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <span>
                          <IconButton
                            onClick={onCancel}
                            disabled={readOnly}
                            size="small"
                            sx={{ color: abyssColors.primary[500] }}
                          >
                            <CloseIcon sx={{ fontSize: '1.5rem' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  ),
                  sx: {
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    color: abyssColors.primary[500],
                  },
                },
              }}
            />
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {existing?.title ?? '-'}
            </Typography>
            {mode === 'Edit' && (
              <Tooltip title={checked ? 'Edit title' : 'Enable to edit'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onStartEdit(keyName)}
                    disabled={readOnly || !checked}
                    sx={{ color: abyssColors.primary[500] }}
                  >
                    <EditIcon sx={{ fontSize: '1.5rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export const ReportConfigList = ({
  mode,
  reportConfig,
  onToggleKey,
  onUpdateTitle,
}: ReportConfigListProps) => {
  const reportConfigByKey = useMemo(
    () => new Map(reportConfig.map((config) => [config.key, config])),
    [reportConfig]
  );

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState<string>('');

  const startEdit = useCallback(
    (keyName: string) => {
      const current = reportConfigByKey.get(keyName)?.title ?? '';
      setEditingKey(keyName);
      setDraftTitle(current);
    },
    [reportConfigByKey]
  );

  const cancelEdit = useCallback(() => {
    setEditingKey(null);
    setDraftTitle('');
  }, []);

  const saveEdit = useCallback(
    async (keyName: string) => {
      const trimmed = draftTitle.trim();
      if (!trimmed || !keyName) return;
      await onUpdateTitle(keyName, trimmed);
      setEditingKey(null);
      setDraftTitle('');
    },
    [draftTitle, onUpdateTitle]
  );

  const handleToggle = useCallback(
    (keyName: string, next: boolean) => {
      if (!next && editingKey === keyName) cancelEdit();
      onToggleKey?.(keyName, next);
    },
    [onToggleKey, editingKey, cancelEdit]
  );

  return (
    <Grid container spacing={12}>
      {COLUMNS.map((keysForColumn, columnIndex) => (
        <Grid key={columnIndex} size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            {keysForColumn.map((keyName, keyIndex) => {
              const existing = reportConfigByKey.get(keyName);
              const checked = Boolean(existing);
              const isEditing = editingKey === keyName;

              return (
                <>
                  <KeyRow
                    mode={mode}
                    keyName={keyName}
                    existing={existing}
                    checked={checked}
                    isEditing={isEditing}
                    draftTitle={isEditing ? draftTitle : existing?.title ?? ''}
                    onToggle={handleToggle}
                    onStartEdit={startEdit}
                    onChangeDraft={setDraftTitle}
                    onSave={saveEdit}
                    onCancel={cancelEdit}
                  />
                  {keyIndex < keysForColumn.length - 1 && <Divider sx={{ mt: 1.5, mb: 0.5 }} />}
                </>
              );
            })}
          </Stack>
        </Grid>
      ))}
    </Grid>
  );
};
